import { normalizeTemplateBuilderDraft } from '@/features/templates/builder';
import type { TemplateBuilderDraft } from '@/types/domain';

interface TemplateDraftCacheEntry {
  savedAt: string;
  draft: TemplateBuilderDraft;
}

function getStorageKey(templateId?: string): string {
  return `woof-woof:template-draft:${templateId ?? 'new'}`;
}

export function readTemplateDraftCache(templateId?: string): TemplateDraftCacheEntry | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(getStorageKey(templateId));

    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue) as TemplateDraftCacheEntry | null;

    if (!parsed || typeof parsed !== 'object' || !parsed.savedAt || !parsed.draft) {
      return null;
    }

    return {
      savedAt: parsed.savedAt,
      draft: normalizeTemplateBuilderDraft(parsed.draft),
    };
  } catch {
    return null;
  }
}

export function writeTemplateDraftCache(templateId: string | undefined, draft: TemplateBuilderDraft): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const payload: TemplateDraftCacheEntry = {
      savedAt: new Date().toISOString(),
      draft: normalizeTemplateBuilderDraft(draft),
    };

    window.localStorage.setItem(getStorageKey(templateId), JSON.stringify(payload));
  } catch {
    // Local draft cache is best-effort only.
  }
}

export function clearTemplateDraftCache(templateId?: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(getStorageKey(templateId));
  } catch {
    // Ignore local cache cleanup failures.
  }
}
